// Mock x402 payment-envelope client.
//
// This stands in for two real backends that don't exist in the frontend yet:
//   1. A MemeGenes content-access route that, when a locked resource is
//      requested WITHOUT payment, returns HTTP 402 with the envelope below.
//   2. meme-mark's PaymentService, which settles a chosen payment option and
//      returns a proof the client replays to unlock.
//
// Everything here is local and mocked — no network. The wire shapes
// (`accepts`, `scheme`, `network`, `asset`, `maxAmountRequired`, `payTo`,
// `resource`) match meme-mark's `402-stripe-mcp-payments.md` ADO so this is a
// drop-in when the real route lands: swap request402() for the 402 response
// body and settle() for the PaymentService call.

/** Provny-internal treasury id credits settle against (the "house" account). */
const CREDITS_PAY_TO = 'provny-treasury';

/** Mock creator wallet crypto settles to. A real envelope carries the live one. */
const CRYPTO_PAY_TO = 'So1anaWa11etProvnyMockPayToAddr00000000000';

/** One way to satisfy a 402 — either Provny credits or an exact USDC transfer. */
export type PaymentOption =
  | {
      scheme: 'credits';
      asset: 'credit';
      /** Amount in credits. Prototype assumes 1 credit = 1¢. */
      maxAmountRequired: number;
      payTo: string;
      resource: string;
    }
  | {
      scheme: 'exact';
      network: 'solana';
      asset: 'USDC';
      /** Amount in USDC, as a decimal string (e.g. "0.03"). */
      maxAmountRequired: string;
      payTo: string;
      resource: string;
    };

/** The body a locked resource "returns" with HTTP 402. */
export type PaymentEnvelope = {
  status: 402;
  resource: string;
  accepts: PaymentOption[];
};

/** The proof the client replays after settling to unlock the content. */
export type PaymentProof = {
  scheme: PaymentOption['scheme'];
  /** Present for crypto settlement — a (mock) Solana transaction signature. */
  txSig?: string;
  /** Present for every settlement — an idempotency/replay nonce. */
  nonce: string;
};

/** Stable resource id for a piece of content, used across the envelope. */
export function resourceId(contentId: string): string {
  return `provny:content:${contentId}`;
}

/**
 * Build the 402 envelope for a locked resource. Mirrors the body the real
 * content route returns when requested without payment: a credits option
 * (1 credit = 1¢) and a Solana/USDC exact option (cents / 100).
 */
export function request402(contentId: string, priceCents: number): PaymentEnvelope {
  const resource = resourceId(contentId);
  return {
    status: 402,
    resource,
    accepts: [
      {
        scheme: 'credits',
        asset: 'credit',
        maxAmountRequired: priceCents,
        payTo: CREDITS_PAY_TO,
        resource,
      },
      {
        scheme: 'exact',
        network: 'solana',
        asset: 'USDC',
        maxAmountRequired: usdcAmount(priceCents),
        payTo: CRYPTO_PAY_TO,
        resource,
      },
    ],
  };
}

/** cents → USDC decimal string, e.g. 3 → "0.03", 30 → "0.30". */
export function usdcAmount(priceCents: number): string {
  return (priceCents / 100).toFixed(2);
}

const BASE58 = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

// SECURITY: Math.random() is fine here — this is cosmetic. A client-minted
// nonce/txSig is NEVER a real settlement proof; the real one is issued by a
// verified backend (meme-mark PaymentService) and validated server-side before
// content is released. Nothing here gates access.
/** A fake base58-ish signature so crypto unlocks look real in the UI. */
function mockTxSig(): string {
  let s = '';
  for (let i = 0; i < 64; i++) s += BASE58[Math.floor(Math.random() * BASE58.length)];
  return s;
}

/**
 * MOCK settlement. A real meme-mark PaymentService would charge the chosen
 * option and hand back a verifiable proof; here we just wait a beat and mint a
 * plausible one. Credits get a nonce; crypto gets a nonce + a mock txSig.
 */
export function settle(option: PaymentOption): Promise<PaymentProof> {
  const nonce = `nonce_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(
        option.scheme === 'credits'
          ? { scheme: 'credits', nonce }
          : { scheme: 'exact', nonce, txSig: mockTxSig() },
      );
    }, 700);
  });
}
